import { PermissionsMap } from '@/composables/auth/useAuthorization';
import { useConfig } from '@/composables/useConfig';
import { useUla } from '@/composables/useUla';
import { router } from '@/router';
import { UlaUser } from '@/types/ula';
import { agwClient } from '@tfpl/api-client';
import { ref, Ref } from 'vue';

import { useWebSocket } from '@/composables/useWebSocket';
import { useActiveCompany } from '../useActiveCompany';
import { usePersonalSettings } from '@/composables/usePersonalSettings';

interface UserPayload {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  uuid: string;
  permissions: agwClient.CompanyPermissionsDto[];
  mappedPermissions: Map<string, PermissionsMap>;
}

const { setCompanies } = useActiveCompany();
const { authAndConnect, destroySocket } = useWebSocket();

/**
 * STATE
 */
const isLogged: Ref<boolean> = ref(false);
const isImpersonated: Ref<boolean> = ref(false);
const isAccessDenied: Ref<boolean> = ref(false);
const initialized = ref(false);
const user: Ref<UserPayload> = ref({
  firstName: '',
  lastName: '',
  email: '',
  avatarUrl: '',
  role: '',
  uuid: '',
  permissions: [],
  mappedPermissions: new Map<string, PermissionsMap>(),
});

/**
 * Controls the authentication process for the application.
 */
export const useAuthentication = () => {
  const { logout: ulaLogout } = useUla();
  const { getConfig } = useConfig();

  /**
   * Sets the logged-in user information.
   *
   * @param {Object} payload - The user profile data and token.
   * @param {string} payload.displayName - The user's display name.
   * @param {string} payload.email - The user's email address.
   * @param {string} payload.photoURL - The URL of the user's avatar.
   * @param {string} payload.uuid - The user's UUID.
   * @param {Array} payload.companyPermissions - The user's company permissions.
   */
  const setLoggedUser = (payload: UlaUser): void => {
    let firstName = '';
    let lastName = '';
    if (payload?.displayName) {
      const nameSplit = payload.displayName.split(' ');
      firstName = nameSplit[0];
      lastName = nameSplit[1];
    }

    const mappedPermissions = mapPermissions(payload.companyPermissions);

    user.value = {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: payload.email,
      avatarUrl: payload.photoURL,
      uuid: payload.uuid,
      permissions: payload.companyPermissions,
      mappedPermissions,
    };

    // Don't wait for the WebSocket connection to settle before continuing login (S9383).
    authAndConnect().catch((e: unknown) => console.error('Failed to connect WebSocket', e));

    isLogged.value = true;
    initialized.value = true;

    if (payload.token) {
      try {
        const segment = payload.token.split('.')[1].replaceAll('-', '+').replaceAll('_', '/');
        const tokenPayload = JSON.parse(atob(segment));
        isImpersonated.value = tokenPayload.isImpersonated === true;
      } catch (e) {
        console.error('Failed to decode JWT token for impersonation check', e);
        isImpersonated.value = false;
      }
    } else {
      isImpersonated.value = false;
    }
  };

  /**
   * Handles login error by resetting state variables and headers.
   * - Sets isLogged to false
   * - Sets token to null
   * - Sets user to an empty object
   * @returns {void}
   */
  const handleLoginError = (): void => {
    isLogged.value = false;
    isImpersonated.value = false;
    user.value = {
      firstName: '',
      lastName: '',
      email: '',
      avatarUrl: '',
      uuid: '',
      permissions: [],
      mappedPermissions: new Map(),
    };
  };

  /**
   * Refreshes the login session using the provided authentication code and organization code.
   *
   * @param payload - An object containing the authentication code and organization code.
   * @param payload.authCode - The authentication code obtained from the user.
   * @param payload.organizationCode - The organization code to which the user belongs.
   * @returns A Promise that resolves to void.
   * @throws An error if login refresh fails.
   */
  const refreshLogin = async (payload: UlaUser): Promise<void> => {
    try {
      isAccessDenied.value = false;
      const { setCompanies } = useActiveCompany();
      const { loadPersonalSettings, state: personalSettings } = usePersonalSettings();
      setLoggedUser(payload);
      await loadPersonalSettings();
      await setCompanies(payload, personalSettings.selectedTenantCode);
    } catch (e) {
      handleLoginError();
      console.error(e);
    }
  };

  /**
   * Logs out the current user.
   *
   * This function performs the following actions:
   *  1. Destroys the WebSocket connection.
   *  2. If logout is coming from app, not from ULA, call the `ulaLogout` function to
   *     log out from the ULA authentication system.
   *  3. Sets the `isLogged` reactive state to `false`.
   *  4. Sets the `token` reactive state to `null`.
   *  5. Resets the `user` reactive state to an empty object with default property values.
   *  6. Navigates the user to the login page using the `router.push` function.
   *
   * @returns {Promise<void>} This function does not return any value.
   */
  const logout = async (notifyUla: boolean): Promise<void> => {
    if (notifyUla) {
      console.log('>>> Notifying ULA about logout...');
      await ulaLogout();
    }
    destroySocket();
    isLogged.value = false;
    isImpersonated.value = false;
    user.value = {
      firstName: '',
      lastName: '',
      email: '',
      avatarUrl: '',
      uuid: '',
      permissions: [],
      mappedPermissions: new Map(),
    };
    globalThis.location.href = `${getConfig().ULA_URL}/?organization=${getConfig().TENANT}&application=ERP&logout=true`;
  };

  const initLoggedUser = async (payloadData: UlaUser) => {
    setLoggedUser(payloadData);
    const { setSelectedTenantCode, loadPersonalSettings, state: personalSettings } = usePersonalSettings();
    setSelectedTenantCode(payloadData.tenant);
    await loadPersonalSettings();
    await setCompanies(payloadData, personalSettings.selectedTenantCode);
    const redirectTo = getConfig().REDIRECT_URL;
    await router.push(redirectTo || '/app/home');
  };

  return {
    isLogged,
    isImpersonated,
    isAccessDenied,
    initialized,
    user,
    logout,
    refreshLogin,
    initLoggedUser,
  };
};

/*
 * Function for mapping company permissions.
 */
const mapPermissions = (companyPermissions: agwClient.CompanyPermissionsDto[]) => {
  const permissionMaps = new Map();

  // one map per company
  for (const permissionSet of companyPermissions) {
    const permissionMap = new Map();

    for (const subject of permissionSet.permissions) {
      const actions = subject.actions.map((action) => action.name);
      permissionMap.set(subject.name, actions);
    }

    permissionMaps.set(permissionSet.code, permissionMap);
  }

  return permissionMaps;
};
